/**
 * Browser AHP relay connection runtime.
 *
 * Owns one `AhpClient` + Web PubSub transport pair for a single Mission Control
 * mediated connection to an environment. It connects, discovers the session to
 * observe, applies the subscription snapshot once, then fans every live action
 * out to the caller. Transport drops trigger a token-refreshed re-establish;
 * the snapshot re-applied on reconnect recovers any missed actions (the
 * consumer's projector is idempotent).
 *
 * Dispatches user turns and tool-call confirmation responses for an observed session.
 */

import {AhpClient, RpcError, type AhpTransport, type Subscription} from '@microsoft/agent-host-protocol/client'
import type {
  ActionEnvelope,
  ActionType,
  ChatState,
  MessageAttachment,
  MessageKind,
  ModelSelection,
  RootState,
  StateAction,
  URI,
} from '@microsoft/agent-host-protocol'
import {WpsTransport, type WebSocketFactory} from './webpubsub/wps-transport'
import {parseGroupName} from './webpubsub/groups'
import {
  buildWpsUrl,
  connectToEnvironment,
  reconnectToEnvironment,
  type MCConnectResponse,
  type MCFetcher,
} from './mission-control/mission-control-environment-client'
import {
  AUTH_REQUIRED_CODE,
  authRequiredResources,
  defaultResourceTokenProvider,
  satisfyResources,
  type ResourceTokenProvider,
} from './protected-resource-auth'

const ROOT_CHANNEL = 'ahp-root://' as const
const ACTION_TURN_STARTED = 'chat/turnStarted' as ActionType.ChatTurnStarted
const MESSAGE_KIND_USER = 'user' as MessageKind.User
const PROTOCOL_VERSIONS = ['0.5.0'] as const
const CHAT_URI_SUFFIX = '/chat' as const
const RECONNECT_BASE_DELAY_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 30_000
const MAX_AUTH_RETRIES = 1

/** The AHP channel for a session, observed over the relay. */
export function sessionChannel(sessionId: string): URI {
  return `ahp-session:/${sessionId}`
}

/**
 * The per-session chat channel. As of AHP 0.4.0 the conversation lives on a
 * chat, and `copilotd` names the session's default chat by appending `/chat` to
 * the session URI. We prefer the session's advertised `defaultChat` when present
 * and fall back to this deterministic form.
 */
export function chatChannelFor(sessionChannelUri: URI): URI {
  return `${sessionChannelUri}${CHAT_URI_SUFFIX}`
}

export type RelayStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error'

export interface RelayLogger {
  info(message: string): void
  error(message: string): void
}

const noopLogger: RelayLogger = {info: () => {}, error: () => {}}

export type AhpClientFactory = (transport: AhpTransport) => AhpClient

const defaultAhpClientFactory: AhpClientFactory = transport => new AhpClient(transport)

export interface RelayConnectionRuntimeOptions {
  environmentId: string
  /** Mission Control session id; the relay observes this session's channel. */
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: URI
  /** Receives the chat snapshot each time it is (re)established. */
  onSnapshot: (state: ChatState) => void
  /** Receives every live action in `serverSeq` order. */
  onAction: (env: ActionEnvelope) => void
  onStatus?: (status: RelayStatus, error?: string) => void
  /** Performs Mission Control requests. Required unless both `mcConnect` and `mcReconnect` are supplied. */
  fetcher?: MCFetcher
  /** Mints WPS credentials. Defaults to the Mission Control connect endpoint. */
  mcConnect?: () => Promise<MCConnectResponse>
  /** Refreshes WPS credentials for reconnect. Defaults to the reconnect endpoint. */
  mcReconnect?: (clientId: string) => Promise<MCConnectResponse>
  webSocketFactory?: WebSocketFactory
  ahpClientFactory?: AhpClientFactory
  /** Resolves per-resource tokens when the host requires authentication. */
  resourceTokenProvider?: ResourceTokenProvider
  logger?: RelayLogger
}

function isChatState(state: unknown): state is ChatState {
  return !!state && typeof state === 'object' && Array.isArray((state as {turns?: unknown}).turns)
}

function isRootState(state: unknown): state is RootState {
  return !!state && typeof state === 'object' && Array.isArray((state as {agents?: unknown}).agents)
}

/** Read the routing-hint chat URI a session points new input at. */
export function defaultChatChannel(state: unknown): URI | undefined {
  if (!state || typeof state !== 'object') return undefined
  const chat = (state as {defaultChat?: unknown}).defaultChat
  return typeof chat === 'string' ? chat : undefined
}

export class RelayConnectionRuntime {
  private readonly logger: RelayLogger
  private readonly ahpClientFactory: AhpClientFactory

  private client: AhpClient | null = null
  private transport: AhpTransport | null = null
  private mcClientId: string | undefined
  private lastSeenServerSeq = 0
  /** Chat channel resolved from the session's `defaultChat`; the conversation
   * (turns, tool calls) is observed and dispatched here as of AHP 0.4.0. */
  private chatChannel: URI | undefined

  /** Bumped on every connect attempt / teardown to invalidate stale async work. */
  private connectGen = 0
  private disposed = false
  private reconnectAttempts = 0
  /**
   * Whether the current `mcClientId` ever reached a healthy `connected` state.
   *
   * Mission Control delivers the daemon spawn (which makes the daemon join this
   * client's `to-host` group and answer `initialize`) ONLY on `/connect`, not on
   * `/reconnect`. So a client whose spawn was dropped or that connected before the
   * daemon's data plane was ready never gets answered, and reconnecting with the
   * same client id loops on the `initialize` timeout forever. We only take the
   * cheap `/reconnect` path once a connection has actually become healthy;
   * otherwise (and after any failed (re)establish) we escalate to a fresh
   * `/connect` so a new client id + new spawn is published.
   */
  private clientEverConnected = false

  private readonly options: RelayConnectionRuntimeOptions

  constructor(options: RelayConnectionRuntimeOptions) {
    this.options = options
    this.logger = options.logger ?? noopLogger
    this.ahpClientFactory = options.ahpClientFactory ?? defaultAhpClientFactory
  }

  private observedChannel(): URI {
    return this.options.channel ?? sessionChannel(this.options.sessionId)
  }

  async connect(): Promise<void> {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    const gen = ++this.connectGen
    this.setStatus('connecting')
    try {
      await this.establish(gen, false)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      // The (re)establish failed before becoming healthy; force the next
      // attempt to take the full `/connect` path (new client id + spawn).
      this.clientEverConnected = false
      const msg = formatError(err)
      this.logger.error(`relay connect failed: ${msg}`)
      this.setStatus('error', msg)
      this.scheduleReconnect(gen)
    }
  }

  /** Dispatch a new user turn on the observed session's chat channel. Requires a live connection. */
  dispatchTurn(text: string, attachments?: MessageAttachment[], model?: ModelSelection): void {
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')
    const action: StateAction = {
      type: ACTION_TURN_STARTED,
      turnId: crypto.randomUUID(),
      message: {
        text,
        origin: {kind: MESSAGE_KIND_USER},
        ...(attachments && attachments.length > 0 ? {attachments} : {}),
        ...(model ? {model} : {}),
      },
    }
    client.dispatch(chat, action)
  }

  /**
   * Force the runtime to drop its current connection and re-establish, which
   * causes the next `subscribe` to return a fresh snapshot. Useful when a new
   * consumer mounts on top of an already-active runtime whose cached snapshot
   * may not reflect the current daemon state (e.g. responses streamed in
   * after the original consumer was torn down).
   */
  async refresh(): Promise<void> {
    if (this.disposed) return
    await this.teardownClient()
    const gen = ++this.connectGen
    this.setStatus('connecting')
    try {
      await this.establish(gen, true)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      this.clientEverConnected = false
      const msg = formatError(err)
      this.logger.error(`relay refresh failed: ${msg}`)
      this.setStatus('error', msg)
      this.scheduleReconnect(gen)
    }
  }

  /** Approve or deny a pending tool call on the observed session's chat channel. Requires a live connection. */
  confirmToolCall({approved, toolCallId, turnId}: {approved: boolean; toolCallId: string; turnId: string}): void {
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')

    const action = approved
      ? {
          type: 'chat/toolCallConfirmed',
          turnId,
          toolCallId,
          approved: true,
          confirmed: 'user-action',
        }
      : {
          type: 'chat/toolCallConfirmed',
          turnId,
          toolCallId,
          approved: false,
          reason: 'denied',
          reasonMessage: 'Denied by user',
        }
    client.dispatch(chat, action as StateAction)
  }

  async dispose(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    this.connectGen++
    await this.teardown()
    this.setStatus('closed')
  }

  private async establish(gen: number, isReconnect: boolean): Promise<void> {
    // Only reuse the cheap `/reconnect` path (which does NOT re-publish the
    // daemon spawn) when this client id already reached a healthy connection.
    // Otherwise mint a fresh client via `/connect` so a new spawn is published.
    const reconnectClientId = isReconnect && this.clientEverConnected ? this.mcClientId : undefined
    const mc =
      reconnectClientId !== undefined
        ? await (this.options.mcReconnect?.(reconnectClientId) ?? this.mcReconnectDefault(reconnectClientId))
        : await (this.options.mcConnect?.() ?? this.mcConnectDefault())
    if (gen !== this.connectGen || this.disposed) return
    this.mcClientId = mc.client_id

    const transport = await this.openTransport(mc)
    if (gen !== this.connectGen || this.disposed) {
      await safeClose(transport)
      return
    }
    this.transport = transport

    const client = this.ahpClientFactory(transport)
    this.client = client
    client.connect()

    const init = await client.initialize({
      clientId: mc.client_id,
      protocolVersions: [...PROTOCOL_VERSIONS],
      initialSubscriptions: [ROOT_CHANNEL],
    })
    if (gen !== this.connectGen || this.disposed) return
    this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, init.serverSeq ?? 0)
    const resourceTokenProvider = this.options.resourceTokenProvider ?? defaultResourceTokenProvider
    await this.authenticateAdvertisedResources(client, init.snapshots, resourceTokenProvider)
    if (gen !== this.connectGen || this.disposed) return

    // AHP 0.4.0 split the conversation off the session: turns/tool calls now
    // live on a chat channel. Subscribe to the session only to discover its
    // `defaultChat`, then observe (and dispatch onto) that chat. If the session
    // snapshot does not advertise a default chat yet (e.g. a freshly
    // MC-bootstrapped session), fall back to the deterministic `<session>/chat`
    // form rather than failing the whole connection.
    const session = await this.subscribeWithAuth(client, this.observedChannel(), resourceTokenProvider)
    if (gen !== this.connectGen || this.disposed) return
    if (session.result.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, session.result.snapshot.fromSeq ?? 0)
    }
    const advertised = defaultChatChannel(session.result.snapshot?.state)
    const chat = advertised ?? chatChannelFor(this.observedChannel())
    if (!advertised) {
      this.logger.info(`relay: session advertised no defaultChat; using derived chat channel ${chat}`)
    }
    this.chatChannel = chat
    // We only needed the session snapshot to resolve the chat; drop the
    // session fan-out so its un-consumed action stream does not buffer.
    await client.unsubscribe(this.observedChannel())
    if (gen !== this.connectGen || this.disposed) return

    const {result, subscription} = await this.subscribeWithAuth(client, chat, resourceTokenProvider)
    if (gen !== this.connectGen || this.disposed) return
    if (result.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, result.snapshot.fromSeq ?? 0)
      if (isChatState(result.snapshot.state)) {
        this.options.onSnapshot(result.snapshot.state)
      }
    }
    this.reconnectAttempts = 0
    this.clientEverConnected = true
    this.setStatus('connected')
    void this.consume(subscription, gen)
  }

  private mcConnectDefault(): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcConnect')
    return connectToEnvironment(this.options.environmentId, this.options.sessionId, this.options.fetcher)
  }

  private mcReconnectDefault(clientId: string): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcReconnect')
    return reconnectToEnvironment(this.options.environmentId, clientId, this.options.fetcher)
  }

  private async authenticateAdvertisedResources(
    client: AhpClient,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    provider: ResourceTokenProvider,
  ): Promise<void> {
    const rootSnapshot = snapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    if (!isRootState(rootSnapshot?.state)) return

    const resources = rootSnapshot.state.agents.flatMap(agent => agent.protectedResources ?? [])
    await satisfyResources(client, resources, provider)
  }

  private async subscribeWithAuth(
    client: AhpClient,
    channel: URI,
    provider: ResourceTokenProvider,
  ): ReturnType<AhpClient['subscribe']> {
    let attempt = 0
    for (;;) {
      try {
        return await client.subscribe(channel)
      } catch (err) {
        if (!(err instanceof RpcError) || err.code !== AUTH_REQUIRED_CODE || attempt >= MAX_AUTH_RETRIES) {
          throw err
        }
        const satisfied = await satisfyResources(client, authRequiredResources(err.data), provider)
        if (!satisfied) throw err
        attempt++
      }
    }
  }

  private async openTransport(mc: MCConnectResponse): Promise<AhpTransport> {
    const minted = parseGroupName(mc.groups.broadcast)
    return WpsTransport.connect({
      url: buildWpsUrl(mc),
      toHostGroup: mc.groups.to_host,
      joinGroups: [mc.groups.broadcast, mc.groups.to_client],
      groupValidation: {expected: {uid: minted.uid, eid: minted.eid}},
      onProtocolError: err => this.logger.error(`relay WPS frame dropped: ${formatError(err)}`),
      ...(this.options.webSocketFactory ? {webSocketFactory: this.options.webSocketFactory} : {}),
    })
  }

  private async consume(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type !== 'action') continue
        this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, evt.params.serverSeq ?? 0)
        try {
          this.options.onAction(evt.params)
        } catch (err) {
          this.logger.error(`relay action listener failed: ${formatError(err)}`)
        }
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay subscription ended: ${formatError(err)}`)
      }
    }
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private scheduleReconnect(gen: number): void {
    if (this.disposed || gen !== this.connectGen) return
    const attempt = this.reconnectAttempts++
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS)
    this.setStatus('reconnecting')
    setTimeout(() => {
      if (this.disposed || gen !== this.connectGen) return
      const reconnectGen = ++this.connectGen
      void this.reestablish(reconnectGen)
    }, delay)
  }

  private async reestablish(gen: number): Promise<void> {
    await this.teardownClient()
    try {
      await this.establish(gen, true)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      // Failed to (re)establish; escalate the next attempt to a full `/connect`
      // (new client id + spawn) rather than reconnecting the same dead client.
      this.clientEverConnected = false
      this.logger.error(`relay reconnect failed: ${formatError(err)}`)
      this.scheduleReconnect(gen)
    }
  }

  private async teardown(): Promise<void> {
    await this.teardownClient()
  }

  private async teardownClient(): Promise<void> {
    const client = this.client
    this.client = null
    this.chatChannel = undefined
    if (client) {
      try {
        await client.shutdown()
      } catch {
        // best-effort
      }
    }
    const transport = this.transport
    this.transport = null
    if (transport) await safeClose(transport)
  }

  private setStatus(status: RelayStatus, error?: string): void {
    this.options.onStatus?.(status, error)
  }
}

/**
 * Strip the Web PubSub `access_token` from any string before it is logged or
 * surfaced. Transport errors can embed the full connection URL (which carries
 * `?access_token=…`) in their message, so redact it defensively at the single
 * point where errors become strings.
 */
function redactSecrets(message: string): string {
  return message.replace(/(access_token=)[^&\s"']+/gi, '$1[REDACTED]')
}

function formatError(err: unknown): string {
  return redactSecrets(err instanceof Error ? err.message : String(err))
}

async function safeClose(transport: AhpTransport): Promise<void> {
  try {
    await transport.close()
  } catch {
    // best-effort
  }
}
